import html2pdf from 'html2pdf.js';
import { PRESS, PRESS_IMAGES } from '../data/press';

// Convert an image URL to a data URL so html2canvas can render it reliably
async function toDataURL(url) {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    console.warn('Image load failed for PDF:', url, e);
    return url;
  }
}

const wc = (t) => t.trim().split(/\s+/).length;

export async function generatePressReleasePDF() {
  const heroImg = await toDataURL(PRESS_IMAGES[0].file);
  const inquisitorImg = await toDataURL(PRESS_IMAGES[1].file);
  const villageImg = await toDataURL(PRESS_IMAGES[3].file);

  const el = document.createElement('div');
  el.style.width = '794px'; // A4 @ 96dpi
  el.style.background = '#f4ecd8';
  el.style.color = '#1a1109';
  el.style.fontFamily = "'EB Garamond', Georgia, serif";
  el.style.boxSizing = 'border-box';

  // Each "page" has a min-height slightly less than an A4 so browser layout
  // doesn't overflow to a second slice. Sections 2..N start on a new page
  // via page-break-before.
  const PAGE_H = 1050; // A4 height @ 96dpi is 1123; leave a bit of slack
  const pageStyle = `
    width:794px;
    min-height:${PAGE_H}px;
    padding:56px 60px;
    box-sizing:border-box;
    position:relative;
    page-break-inside: avoid;
  `;
  const nextPageStyle = pageStyle + ' page-break-before: always;';

  el.innerHTML = `
    <style>
      .pr { color:#1a1109; }
      .pr h1, .pr h2, .pr h3, .pr .display { font-family: 'Cinzel', Georgia, serif; letter-spacing: 0.02em; margin:0; }
      .pr .sc { font-family: 'IM Fell English SC','Cinzel',serif; letter-spacing: 0.28em; text-transform: uppercase; }
      .pr .rule { height:1px; background:#8b5a2a; opacity:0.4; margin: 14px 0; }
      .pr .ornate { text-align:center; letter-spacing:0.5em; color:#8b5a2a; font-size:10px; }
      .pr .cap { font-size: 10px; color:#7a4a20; font-style: italic; margin-top: 6px; line-height:1.5; }
      .pr .kicker { font-size: 10px; letter-spacing:0.4em; color:#8b3a2a; text-transform:uppercase; }
      .pr p { font-size: 13px; line-height: 1.7; margin: 0 0 12px; }
      .pr h3.section {
        font-size:12px;
        letter-spacing:0.32em;
        text-transform:uppercase;
        color:#7a4a20;
        margin: 6px 0 8px;
      }
      .pr .pull {
        border-left: 3px solid #8b5a2a;
        padding: 10px 16px;
        font-style: italic;
        font-size: 13.5px;
        color:#3a2a18;
        margin: 14px 0;
        page-break-inside: avoid;
      }
      .pr .review {
        border-left: 2px solid #c8a06a;
        padding: 8px 14px;
        font-style: italic;
        font-size: 12.5px;
        color:#3a2a18;
        margin: 10px 0;
        page-break-inside: avoid;
      }
      .pr .facts { width:100%; border-collapse:collapse; page-break-inside: avoid; }
      .pr .facts td { padding: 5px 10px; font-size: 12px; vertical-align: top; line-height:1.5; }
      .pr .facts td.k { color:#7a4a20; font-family:'IM Fell English SC','Cinzel',serif; letter-spacing:0.18em; text-transform:uppercase; width: 34%; font-size:10px; }
      .pr .blurb-card {
        background:#eadfc0;
        border:1px solid #c8a06a;
        padding:14px 18px;
        margin-bottom:12px;
        page-break-inside: avoid;
      }
      .pr .foot { font-size: 10px; color:#7a4a20; line-height:1.6; }
    </style>
    <div class="pr">

      <!-- ============================== PAGE 1 — COVER ============================== -->
      <section style="${pageStyle}">

        <div style="display:flex; justify-content:space-between; align-items:flex-start;">
          <div>
            <div class="kicker">${PRESS.releaseType}</div>
            <div style="font-size:11px; color:#7a4a20; margin-top:4px;">${PRESS.dateline}</div>
          </div>
          <div style="text-align:right; font-size:10px; color:#7a4a20; letter-spacing:0.25em; text-transform:uppercase;">
            COLAB Theatre<br/>Press Release
          </div>
        </div>

        <div class="rule"></div>
        <div class="ornate">† · A COLAB THEATRE PRODUCTION · †</div>
        <div class="rule"></div>

        <h1 style="font-size:60px; line-height:1; margin:36px 0 8px; letter-spacing:0.06em; color:#1a1109;">ASH</h1>
        <div class="sc" style="font-size:12px; color:#5a3d20;">The Witch Trials of Drudenhaus</div>

        <h2 style="font-size:18px; line-height:1.35; margin:22px 0 10px; font-weight:600;">
          ${PRESS.headline}
        </h2>
        <p style="font-style:italic; color:#3a2a18; font-size:13.5px; margin-bottom:20px;">${PRESS.strap}</p>

        <img src="${heroImg}" style="width:100%; height:360px; object-fit:cover; border:1px solid #8b5a2a; display:block;" />
        <div class="cap">${PRESS_IMAGES[0].caption}</div>

        <div style="position:absolute; bottom:36px; left:60px; right:60px; text-align:center;">
          <div class="ornate" style="font-size:9px; letter-spacing:0.6em;">† · CONTINUED · †</div>
        </div>

      </section>

      <!-- ============================== PAGE 2 — STORY ============================== -->
      <section style="${nextPageStyle}">

        <div class="kicker">The story</div>
        <h2 style="font-family:'Cinzel',Georgia,serif; font-size:22px; margin:6px 0 14px;">Face the Inquisitor.</h2>
        <div class="rule"></div>

        <p style="font-size:13.5px;"><b>LONDON.</b> ${PRESS.lede}</p>
        <p>${PRESS.body[0]}</p>
        <p>${PRESS.body[1]}</p>

        <div class="pull">
          ${PRESS.quote.text}<br/>
          <span style="font-size:10.5px; letter-spacing:0.25em; text-transform:uppercase; color:#7a4a20; font-style:normal;">— ${PRESS.quote.attribution}</span>
        </div>

        <p>${PRESS.body[2]}</p>

      </section>

      <!-- ============================== PAGE 3 — PHOTOGRAPHY + FACTS ============================== -->
      <section style="${nextPageStyle}">

        <div class="kicker">Photography</div>
        <h3 class="section" style="margin-bottom:12px;">Production stills</h3>

        <div style="display:flex; gap:14px; page-break-inside:avoid;">
          <div style="width:49%;">
            <img src="${inquisitorImg}" style="width:100%; height:260px; object-fit:cover; border:1px solid #8b5a2a; display:block;" />
            <div class="cap">${PRESS_IMAGES[1].caption}</div>
          </div>
          <div style="width:49%;">
            <img src="${villageImg}" style="width:100%; height:260px; object-fit:cover; border:1px solid #8b5a2a; display:block;" />
            <div class="cap">${PRESS_IMAGES[3].caption}</div>
          </div>
        </div>

        <h3 class="section" style="margin-top:26px;">Key facts</h3>
        <div class="rule"></div>
        <table class="facts">
          ${PRESS.keyFacts
            .map(
              (f) =>
                `<tr><td class="k">${f.label}</td><td>${f.value}</td></tr>`
            )
            .join('')}
        </table>

      </section>

      <!-- ============================== PAGE 4 — REVIEWS + CONTACT + ABOUT ============================== -->
      <section style="${nextPageStyle}">

        <div class="kicker">Early word</div>
        <h3 class="section">From audiences</h3>
        <div class="rule"></div>
        ${PRESS.reviews
          .map(
            (r) => `
            <div class="review">
              &ldquo;${r.quote}&rdquo;<br/>
              <span style="font-size:10px; letter-spacing:0.25em; text-transform:uppercase; color:#7a4a20; font-style:normal;">— ${r.attribution}</span>
            </div>`
          )
          .join('')}

        <h3 class="section" style="margin-top:24px;">Press contact</h3>
        <div class="rule"></div>
        <p style="page-break-inside:avoid;">
          <b>${PRESS.press.name}</b><br/>
          Email: <a href="mailto:${PRESS.press.email}" style="color:#7a4a20;">${PRESS.press.email}</a><br/>
          Phone: ${PRESS.press.phone}<br/>
          Web: ${PRESS.press.web}
        </p>

        <h3 class="section" style="margin-top:22px;">About COLAB Theatre</h3>
        <div class="rule"></div>
        <p style="page-break-inside:avoid;">${PRESS.boilerplate}</p>

      </section>

      <!-- ============================== PAGE 5 — BLURBS ============================== -->
      <section style="${nextPageStyle}">

        <div class="kicker">Ready-made blurbs</div>
        <h2 style="font-family:'Cinzel',Georgia,serif; font-size:22px; margin:6px 0 6px;">For listings & ticketing sites</h2>
        <p style="font-style:italic; color:#7a4a20; margin: 0 0 16px; font-size:11.5px;">
          Copy and paste — approved short and long descriptions for editorial use.
        </p>

        <div class="blurb-card">
          <div style="display:flex; justify-content:space-between; align-items:baseline;">
            <div class="sc" style="color:#7a4a20; font-size:10.5px;">Short blurb</div>
            <div style="font-size:10px; color:#7a4a20; font-style:italic;">${wc(PRESS.blurbs.fifty)} words</div>
          </div>
          <p style="margin-top:10px; font-size:12.5px; line-height:1.7;">${PRESS.blurbs.fifty}</p>
        </div>

        <div class="blurb-card">
          <div style="display:flex; justify-content:space-between; align-items:baseline;">
            <div class="sc" style="color:#7a4a20; font-size:10.5px;">Long blurb</div>
            <div style="font-size:10px; color:#7a4a20; font-style:italic;">${wc(PRESS.blurbs.twoHundred)} words</div>
          </div>
          <p style="margin-top:10px; font-size:12px; line-height:1.7; white-space:pre-line;">${PRESS.blurbs.twoHundred}</p>
        </div>

        <div style="position:absolute; bottom:44px; left:60px; right:60px;">
          <div class="ornate">— ends —</div>
          <div class="rule"></div>
          <div class="foot" style="text-align:center;">
            Press images and hi-res photography are available on request via the press office
            and downloadable as an image pack and crop pack at ashthewitchtrials.co.uk.
          </div>
        </div>

      </section>

    </div>
  `;

  document.body.appendChild(el);

  const opt = {
    margin: 0,
    filename: 'ASH-Press-Release-COLAB-Theatre.pdf',
    image: { type: 'jpeg', quality: 0.95 },
    html2canvas: { scale: 2, useCORS: true, backgroundColor: '#f4ecd8' },
    jsPDF: { unit: 'pt', format: 'a4', orientation: 'portrait' },
    pagebreak: { mode: ['css', 'legacy'] }
  };

  try {
    await html2pdf().set(opt).from(el).save();
  } finally {
    document.body.removeChild(el);
  }
}
